import { ColumnSizingState, Updater } from '@tanstack/react-table';
import { useReportBlockConfig } from '@va/ui/components/report-block';
import { useWindowDimensions } from '@va/util/hooks';
import { PropsWithChildren, createContext, useContext, useMemo } from 'react';
import { useColumnSizing } from './useColumnSizing';

type ColumnSizingContextValue = {
  isProvided: boolean;
  columnSizing: ColumnSizingState;
  storedColumnSizing: ColumnSizingState;
  onColumnSizingChange: (u: Updater<ColumnSizingState>, stateOnlyUpdate?: boolean) => void;
};

const ColumnSizingCtx = createContext<ColumnSizingContextValue>({
  isProvided: false,
  columnSizing: {},
  storedColumnSizing: {},
  onColumnSizingChange: () => {},
});

type ColumnSizingProviderProps = PropsWithChildren<{
  reportBlockId: string;
  selectedView: string;
}>;

export const ColumnSizingProvider = ({ reportBlockId, selectedView, children }: ColumnSizingProviderProps) => {
  const { storageNamespace } = useReportBlockConfig();
  const { isTablet } = useWindowDimensions();

  const { columnSizing, storedColumnSizing, onColumnSizingChange } = useColumnSizing({
    namespace: storageNamespace,
    reportBlockId,
    selectedView,
    disabled: isTablet,
  });

  const value = useMemo<ColumnSizingContextValue>(
    () => ({ isProvided: !isTablet, columnSizing, storedColumnSizing, onColumnSizingChange }),
    [isTablet, columnSizing, storedColumnSizing, onColumnSizingChange],
  );

  return <ColumnSizingCtx.Provider value={value}>{children}</ColumnSizingCtx.Provider>;
};

export const useColumnSizingContext = () => useContext(ColumnSizingCtx);
